import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const API_BASE = '/planner-api';

// Optimize VRP
export const optimizeVRP = createAsyncThunk('vrp/optimize', async ({ token, params }) => {
  const res = await fetch(`${API_BASE}/vrp/optimize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Erro na optimização VRP');
  }
  return res.json();
});

// Fetch saved VRP plans
export const fetchVrpPlans = createAsyncThunk('vrp/fetchPlans', async (token) => {
  const res = await fetch(`${API_BASE}/vrp/plans`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Erro ao obter planos VRP');
  return res.json();
});

// Dispatch a VRP plan (create routes and assign)
export const dispatchVrpPlan = createAsyncThunk('vrp/dispatch', async ({ token, planId }) => {
  const res = await fetch(`${API_BASE}/vrp/plans/${planId}/dispatch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Erro ao despachar plano VRP');
  }
  return res.json();
});

const vrpSlice = createSlice({
  name: 'vrp',
  initialState: {
    plans: [],
    currentPlan: null,
    optimizing: false,
    dispatching: false,
    loading: false,
    error: null,
  },
  reducers: {
    clearCurrentPlan: (state) => {
      state.currentPlan = null;
      state.error = null;
    },
    clearVrpError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // optimizeVRP
      .addCase(optimizeVRP.pending, (state) => {
        state.optimizing = true;
        state.error = null;
      })
      .addCase(optimizeVRP.fulfilled, (state, action) => {
        state.optimizing = false;
        state.currentPlan = action.payload;
        // Also add to plans list if it has an id
        if (action.payload?.id) {
          const idx = state.plans.findIndex(p => p.id === action.payload.id);
          if (idx >= 0) {
            state.plans[idx] = action.payload;
          } else {
            state.plans.unshift(action.payload);
          }
        }
      })
      .addCase(optimizeVRP.rejected, (state, action) => {
        state.optimizing = false;
        state.error = action.error?.message || 'Erro na optimização';
      })
      // fetchVrpPlans
      .addCase(fetchVrpPlans.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVrpPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = Array.isArray(action.payload) ? action.payload : action.payload.data || [];
      })
      .addCase(fetchVrpPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Erro ao obter planos';
      })
      // dispatchVrpPlan
      .addCase(dispatchVrpPlan.pending, (state) => {
        state.dispatching = true;
        state.error = null;
      })
      .addCase(dispatchVrpPlan.fulfilled, (state, action) => {
        state.dispatching = false;
        // Update plan status in list
        if (action.payload?.id) {
          const idx = state.plans.findIndex(p => p.id === action.payload.id);
          if (idx >= 0) state.plans[idx] = action.payload;
        }
        if (state.currentPlan?.id === action.payload?.id) {
          state.currentPlan = action.payload;
        }
      })
      .addCase(dispatchVrpPlan.rejected, (state, action) => {
        state.dispatching = false;
        state.error = action.error?.message || 'Erro ao despachar';
      });
  },
});

export const { clearCurrentPlan, clearVrpError } = vrpSlice.actions;

export const selectVrpPlans = state => state.vrp.plans;
export const selectVrpCurrentPlan = state => state.vrp.currentPlan;
export const selectVrpOptimizing = state => state.vrp.optimizing;
export const selectVrpDispatching = state => state.vrp.dispatching;
export const selectVrpLoading = state => state.vrp.loading;
export const selectVrpError = state => state.vrp.error;

export default vrpSlice.reducer;
